import {
	Box,
	Typography,
	Stack,
	debounce,
	StackProps,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import {
	useEffect,
	useRef,
	useState,
	useMemo,
} from 'react';
import { useTheme } from '@mui/material/styles';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';
import { buildMultiLineChartOptions } from '../chartWidgetUtility';

type LineSeries = {
	key: string;
	name: string;
	color?: string;
	smooth?: boolean;
	symbolSize?: number;
};

interface MultiLineChartProps {
	title: any;
	subTitle?: string;
	isLoading?: boolean;
	data: any[];
	containerProps?: StackProps;
	isCost?: boolean;
	xKey?: string;
	xAxisLabel?: string;
	series: LineSeries[];
	tooltipLabel?: any;
	hasTooltip?: boolean;
	showGridLines?: boolean;
	tooltipFormatter?: (params: any) => string;
	lastUpdated?: string;
	centerYAtZero?: boolean;
	legendPosition?: 'top' | 'bottom';
	forecastStartMonth?: string;
	yMin?: number;
	yMax?: number;
	yInterval?: number;
	height?: number;
	showSeriesTotal?: boolean;
	showEndTotal?: boolean;
	showValuesInMillions?: boolean;
	yAxisFormatter?: (value: number) => string;
	onLineClick?: (params: any) => void;
	showLegend?: boolean;
}

const MultiLineChartWidget = ({
	title,
	subTitle,
	isLoading,
	data,
	containerProps,
	isCost = false,
	xKey = 'name',
	xAxisLabel = '',
	series,
	tooltipLabel,
	hasTooltip = true,
	showGridLines = false,
	tooltipFormatter,
	lastUpdated,
	centerYAtZero = false,
	legendPosition = 'top',
	forecastStartMonth,
	yMin,
	yMax,
	yInterval,
	height = 450,
	showSeriesTotal = false,
	showEndTotal = false,
	showValuesInMillions = false,
	yAxisFormatter,
	onLineClick,
	showLegend = true
}: MultiLineChartProps) => {
	const theme = useTheme();
	const colors = theme.palette;
	const [windowSize, setWindowSize] = useState(
		window.innerWidth,
	);
	const chartRef = useRef<HTMLDivElement>(null);

	const options = useMemo(() => {
		const chartOptions: any = buildMultiLineChartOptions({
			data,
			colors,
			showGridLines,
			isCost,
			xKey,
			xAxisLabel,
			series,
			tooltipFormatter,
			lastUpdated,
			centerYAtZero,
			legendPosition,
			forecastStartMonth,
			yMin,
			yMax,
			yInterval,
			showSeriesTotal,
			showEndTotal,
			showValuesInMillions,
			yAxisFormatter
		});

		if (!chartOptions) {
			return chartOptions;
		}

		const applyYAxisFormatter = (yAxis: any) => ({
			...yAxis,
			axisLabel: {
				...yAxis?.axisLabel,
				...(yAxisFormatter && {
					formatter: yAxisFormatter,
				}),
			},
		});

		return {
			...chartOptions,
			legend: Array.isArray(chartOptions.legend)
				? chartOptions.legend.map((legend: any) => ({
					...legend,
					show: showLegend,
				}))
				: {
					...chartOptions.legend,
					show: showLegend,
				},
			yAxis: Array.isArray(chartOptions.yAxis)
				? chartOptions.yAxis.map(applyYAxisFormatter)
				: applyYAxisFormatter(chartOptions.yAxis),
		};
	}, [
		data,
		colors,
		showGridLines,
		isCost,
		xKey,
		xAxisLabel,
		series,
		tooltipFormatter,
		lastUpdated,
		centerYAtZero,
		legendPosition,
		forecastStartMonth,
		yMin,
		yMax,
		yInterval,
		showSeriesTotal,
		showEndTotal,
		showValuesInMillions,
		yAxisFormatter,
		showLegend
	]);

	const chartEvents = useMemo(
		() => ({
			click: (params: any) => {
				onLineClick?.(params);
			},
		}),
		[onLineClick],
	);

	useEffect(() => {
		const onResize = debounce(
			() => setWindowSize(window.innerWidth),
			200,
		);

		window.addEventListener('resize', onResize);

		return () => {
			window.removeEventListener('resize', onResize);
			onResize.clear();
		};
	}, []);

	const renderChartContent = () => {
		if (isLoading) {
			return <LoaderComponent pt={20} />;
		}

		if (!options) {
			return (
				<Stack
					height="100%"
					alignItems="center"
					justifyContent="center"
				>
					<Typography
						variant="bodyText"
						sx={{
							color: colors.widget.text.main,
						}}
					>
						No Data Available
					</Typography>
				</Stack>
			);
		}

		return (
			<ReactECharts
				option={options}
				notMerge
				style={{
					width: '100%',
					height: '100%',
				}}
				onEvents={chartEvents}
			/>
		);
	};

	return (
		<WidgetWraper>
			<Box
				sx={{
					backgroundColor: `${colors.widget.background.card} !important`,
					width: '100%',
				}}
				{...containerProps}
			>
				<Stack gap={2}>
					<Stack direction="row" alignItems="center">
						<Typography
							variant="widgetTitleNew"
							sx={{
								color: colors.widget.text.title,
							}}
						>
							<HighlightedText text={title} />
						</Typography>

						{hasTooltip && tooltipLabel && (
							<InfoTooltip label={tooltipLabel} />
						)}
					</Stack>

					{subTitle && (
						<Typography
							variant="bodyText"
							sx={{
								color: colors.widget.text.title,
							}}
						>
							{subTitle}
						</Typography>
					)}
				</Stack>

				<Box
					mt={2}
					height={height}
					key={windowSize}
					ref={chartRef}
				>
					{renderChartContent()}
				</Box>
			</Box>
		</WidgetWraper>
	);
};

export default MultiLineChartWidget;